import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTenant } from './TenantContext';

interface ThemeContextType {
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    accentColor: string;
    fontFamily: string;
    logoUrl?: string;
    applyTheme: (theme: Partial<ThemeConfig>) => void;
}

interface ThemeConfig {
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    accentColor: string;
    fontFamily: string;
    logoUrl?: string;
}

const defaultTheme: ThemeConfig = {
    primaryColor: '#3b82f6',
    secondaryColor: '#8b5cf6',
    backgroundColor: '#ffffff',
    accentColor: '#3b82f6',
    fontFamily: 'Inter',
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const { tenant } = useTenant();
    const [theme, setTheme] = useState<ThemeConfig>(defaultTheme);

    useEffect(() => {
        if (tenant) {
            const newTheme: ThemeConfig = {
                primaryColor: tenant.primaryColor || defaultTheme.primaryColor,
                secondaryColor: tenant.secondaryColor || defaultTheme.secondaryColor,
                backgroundColor: tenant.backgroundColor || defaultTheme.backgroundColor,
                accentColor: tenant.accentColor || defaultTheme.accentColor,
                fontFamily: tenant.fontFamily || defaultTheme.fontFamily,
                logoUrl: tenant.logoUrl,
            };
            setTheme(newTheme);
            applyThemeToDOM(newTheme);
        }
    }, [tenant]);

    const applyThemeToDOM = (themeConfig: ThemeConfig) => {
        const root = document.documentElement;
        root.style.setProperty('--primary-color', themeConfig.primaryColor);
        root.style.setProperty('--secondary-color', themeConfig.secondaryColor);
        root.style.setProperty('--background-color', themeConfig.backgroundColor);
        root.style.setProperty('--accent-color', themeConfig.accentColor);
        root.style.setProperty('--font-family', themeConfig.fontFamily);

        // Calculate contrast and surface colors
        const isDark = isColorDark(themeConfig.backgroundColor);

        if (isDark) {
            root.style.setProperty('--text-primary', '#ffffff');
            root.style.setProperty('--text-secondary', '#e5e7eb'); // gray-200 - improved contrast
            root.style.setProperty('--text-muted', '#9ca3af'); // gray-400
            root.style.setProperty('--surface-color', adjustColorBrightness(themeConfig.backgroundColor, 15)); // slightly lighter
            root.style.setProperty('--surface-elevated', adjustColorBrightness(themeConfig.backgroundColor, 25)); // for cards/modals
            root.style.setProperty('--border-color', '#4b5563'); // gray-600 - improved visibility
            root.style.setProperty('--shadow-color', 'rgba(0, 0, 0, 0.5)');
        } else {
            root.style.setProperty('--text-primary', '#111827'); // gray-900
            root.style.setProperty('--text-secondary', '#4b5563'); // gray-600 - better contrast
            root.style.setProperty('--text-muted', '#9ca3af'); // gray-400
            root.style.setProperty('--surface-color', '#ffffff');
            root.style.setProperty('--surface-elevated', '#f9fafb'); // gray-50
            root.style.setProperty('--border-color', '#e5e7eb'); // gray-200
            root.style.setProperty('--shadow-color', 'rgba(0, 0, 0, 0.1)');
        }

        // Load Google Font if needed
        if (themeConfig.fontFamily && themeConfig.fontFamily !== 'Inter') {
            loadGoogleFont(themeConfig.fontFamily);
        }
    };

    // Helper to check if color is dark
    const isColorDark = (hex: string) => {
        const rgb = hexToRgb(hex);
        if (!rgb) return false;
        // YIQ equation
        const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
        return brightness < 128;
    };

    // Helper to lighten/darken color
    const adjustColorBrightness = (hex: string, percent: number) => {
        const rgb = hexToRgb(hex);
        if (!rgb) return hex;

        const r = Math.min(255, Math.max(0, rgb.r + (rgb.r * percent / 100)));
        const g = Math.min(255, Math.max(0, rgb.g + (rgb.g * percent / 100)));
        const b = Math.min(255, Math.max(0, rgb.b + (rgb.b * percent / 100)));

        const toHex = (n: number) => {
            const h = Math.round(n).toString(16);
            return h.length === 1 ? '0' + h : h;
        };

        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };

    const loadGoogleFont = (fontFamily: string) => {
        const fontLink = document.getElementById('google-font') as HTMLLinkElement;
        const fontUrl = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(' ', '+')}:wght@300;400;500;600;700&display=swap`;

        if (fontLink) {
            fontLink.href = fontUrl;
        } else {
            const link = document.createElement('link');
            link.id = 'google-font';
            link.rel = 'stylesheet';
            link.href = fontUrl;
            document.head.appendChild(link);
        }
    };

    const applyTheme = (partialTheme: Partial<ThemeConfig>) => {
        const newTheme = { ...theme, ...partialTheme };
        setTheme(newTheme);
        applyThemeToDOM(newTheme);
    };

    return (
        <ThemeContext.Provider value={{ ...theme, applyTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider');
    }
    return context;
}
